import {
  CommunityGroupFilterGroupFragment,
  useCommunityGroupFilterGroupsQuery,
} from '@/generated/graphql';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {edgesToNodes} from '@ark/react';
import {useSelectedCommunities} from '@ark/web';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

export type CommunityGroupItem = CommunityGroupFilterGroupFragment;

type CommunityGroupContextType = {
  groups: CommunityGroupItem[];
  selectedGroupIds: string[];
  setSelectedGroupIds: Dispatch<SetStateAction<string[]>>;
};

const defaultContext: CommunityGroupContextType = {
  groups: [],
  selectedGroupIds: [],
  setSelectedGroupIds: () => {},
};

const CommunityGroupContext =
  createContext<CommunityGroupContextType>(defaultContext);

export const useCommunityGroups = () => {
  const context = useContext(CommunityGroupContext);
  if (!context) {
    throw new Error(
      'useCommunityGroups must be used within a CommunityGroupProvider',
    );
  }
  return context;
};

export const useSelectedGroupIds = (): string[] => {
  const {groups, selectedGroupIds} = useCommunityGroups();
  return useMemo(() => {
    const groupIds = new Set(groups.map(g => g.id));
    return selectedGroupIds.filter(id => groupIds.has(id));
  }, [groups, selectedGroupIds]);
};

type CommunityGroupProviderProps = {
  children: ReactNode;
};

export const CommunityGroupProvider = ({
  children,
}: CommunityGroupProviderProps) => {
  const communityIds = useSelectedCommunities();
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [features, featuresLoading] = useFeatures();
  const isCommunityGroupsFilterEnabled =
    Boolean(features.CommunityGroupFilter) && !featuresLoading;

  const {data} = useCommunityGroupFilterGroupsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isCommunityGroupsFilterEnabled,
  });

  const allGroups = useMemo(
    () => edgesToNodes(data?.communityGroupsAll?.edges) as CommunityGroupItem[],
    [data?.communityGroupsAll?.edges],
  );

  const groups = useMemo(() => {
    if (!isCommunityGroupsFilterEnabled) return [];
    if (communityIds.length === 0) return allGroups;

    const communityIdSet = new Set(communityIds);
    return allGroups.filter(
      group => group.communityId && communityIdSet.has(group.communityId),
    );
  }, [allGroups, communityIds, isCommunityGroupsFilterEnabled]);

  const value = useMemo(
    () => ({groups, selectedGroupIds, setSelectedGroupIds}),
    [groups, selectedGroupIds],
  );

  return (
    <CommunityGroupContext.Provider value={value}>
      {children}
    </CommunityGroupContext.Provider>
  );
};
